import { useCallback, useEffect, useMemo, useRef } from 'react'

import { TabManager } from './tab-manager'
import { EventType, IEventHandler, IIdleTimer } from './types'
import { DEFAULT_ELEMENT, DEFAULT_EVENTS, throttleFn } from './utils'

const BROADCAST_SYNC_INTERVALL = 2000
const ACTION_EVENT_INTERVALL = 500

interface Props {
  timeout: number
  promptBeforeIdle: number
  onIdle: () => void
  onPrompt: () => void
  onAction: (event?: EventType, idleTimer?: IIdleTimer, remote?: boolean) => void
}

export function useIdleTimer(props: Props) {
  // Time references
  const idleTime = useRef<number>(0)
  const lastIdle = useRef<number | null>(null)
  const totalIdleTime = useRef<number>(0)
  const promptTime = useRef<number>(0)
  const remaining = useRef<number>(0)
  const lastActive = useRef<number | null>(null)

  // State references
  const idle = useRef<boolean>(false)
  const prompted = useRef<boolean>(false)
  const paused = useRef<boolean>(false)
  const firstLoad = useRef<boolean>(true)
  const eventsBound = useRef<boolean>(false)
  const tId = useRef<number | null>(null)

  // Tab manager
  const manager = useRef<TabManager | null>(null)

  // Prop references
  const timeoutRef = useRef<number>(props.timeout)
  const promptTimeoutRef = useRef<number>(0)

  // Events
  const sendSyncEvent = useRef<() => void>()
  const emitOnPrompt = useRef<IEventHandler>(props.onPrompt)
  const emitOnIdle = useRef<IEventHandler>(props.onIdle)
  const emitOnAction = useRef<IEventHandler>(props.onAction)

  useEffect(() => {
    if (props.promptBeforeIdle) {
      timeoutRef.current = Math.max(60000, props.timeout - props.promptBeforeIdle)
      promptTimeoutRef.current = props.promptBeforeIdle
    } else {
      timeoutRef.current = props.timeout
      promptTimeoutRef.current = 0
    }
  }, [props.timeout, props.promptBeforeIdle])

  useEffect(() => {
    sendSyncEvent.current = throttleFn(() => {
      manager.current?.active()
    }, BROADCAST_SYNC_INTERVALL)
  }, [])

  useEffect(() => {
    emitOnPrompt.current = props.onPrompt
  }, [props.onPrompt])

  useEffect(() => {
    emitOnIdle.current = props.onIdle
  }, [props.onIdle])

  useEffect(() => {
    emitOnAction.current = props.onAction
  }, [props.onAction])

  useEffect(() => {
    const eventsWereBound = eventsBound.current
    if (eventsWereBound) unbindEvents()

    handleEvent.current = eventHandler
    if (eventsWereBound) bindEvents()
  }, [])

  useEffect(() => {
    const beforeunload = () => {
      if (manager.current) manager.current.close()
      if (callOnAction.cancel) callOnAction.cancel()
      destroyTimeout()
      unbindEvents(true)
    }

    window.addEventListener('beforeunload', beforeunload)

    return () => {
      window.removeEventListener('beforeunload', beforeunload)
      if (manager.current) manager.current.close()
      if (callOnAction.cancel) callOnAction.cancel()
      destroyTimeout()
      unbindEvents(true)
    }
  }, [])

  const callOnAction = useMemo<IEventHandler>(() => {
    const call = (e: EventType, it: IIdleTimer, remote?: boolean) => emitOnAction.current(e, it, remote)
    return throttleFn(call, ACTION_EVENT_INTERVALL)
  }, [])

  const destroyTimeout = (): void => {
    if (tId.current !== null) {
      clearTimeout(tId.current)
      tId.current = null
    }
  }

  const createTimeout = (time?: number, setLastActive = true): void => {
    destroyTimeout()
    tId.current = setTimeout(toggleIdleState, time || timeoutRef.current) as any
    if (setLastActive) lastActive.current = Date.now()
  }

  const togglePrompted = (event?: EventType): void => {
    if (!prompted.current && !idle.current) {
      emitOnPrompt.current(event, idleTimer)
    }

    remaining.current = 0
    promptTime.current = Date.now()
    prompted.current = true
    createTimeout(promptTimeoutRef.current, false)
  }

  const toggleIdle = () => {
    destroyTimeout()
    if (!idle.current) {
      emitOnIdle.current(undefined, idleTimer)
    }
    idle.current = true
    lastIdle.current = Date.now()
    if (prompted.current) {
      promptTime.current = 0
      prompted.current = false
    }
  }

  const toggleActive = (remote?: boolean) => {
    destroyTimeout()
    idle.current = false
    idleTime.current += Date.now() - (lastIdle.current ?? 0)
    totalIdleTime.current += Date.now() - (lastIdle.current ?? 0)
    bindEvents()
    createTimeout()

    if (remote) callOnAction(undefined, undefined, remote)
  }

  const toggleIdleState = (event?: EventType): void => {
    const nextIdle = !idle.current

    if (nextIdle) {
      if (callOnAction.cancel) callOnAction.cancel()
      const elapsed = Date.now() - (lastActive.current ?? 0)
      const skipPrompt = timeoutRef.current + promptTimeoutRef.current < elapsed

      if (!skipPrompt && promptTimeoutRef.current > 0 && !prompted.current) {
        if (manager.current) {
          manager.current.prompt()
        } else {
          togglePrompted(event)
        }
        return
      }

      if (manager.current) {
        manager.current.idle()
      } else {
        toggleIdle()
      }

      return
    }

    if (manager.current) {
      manager.current.active()
    } else {
      toggleActive()
    }
  }

  const eventHandler = (event?: EventType): void => {
    if (!lastActive.current) {
      lastActive.current = Date.now()
    }

    // Notify aciton
    callOnAction(event, idleTimer)

    // The prompt is open so whe only notify that an actino has been made
    if (prompted.current) return

    // Clear any existing timeout
    destroyTimeout()

    // Calculate last time user was active, as we can't rely on the setTimeout ticking at the correct interval
    const elapsedTimeSinceLastActive = Date.now() - lastActive.current

    // If the use is idle or the last active time is more than timeout flip the idle state
    if (idle.current || (!idle.current && elapsedTimeSinceLastActive >= timeoutRef.current)) {
      toggleIdleState(event)
      return
    }

    paused.current = false
    remaining.current = 0
    promptTime.current = 0

    createTimeout()

    if (sendSyncEvent.current) sendSyncEvent.current()
  }

  const handleEvent = useRef<IEventHandler>(eventHandler)

  const bindEvents = (): void => {
    if (!eventsBound.current) {
      DEFAULT_EVENTS.forEach((e) => {
        DEFAULT_ELEMENT.addEventListener(e as any, handleEvent.current, {
          capture: true,
          passive: true,
        })
      })
      eventsBound.current = true
    }
  }

  const unbindEvents = (force = false): void => {
    if (eventsBound.current || force) {
      DEFAULT_EVENTS.forEach((e) => {
        DEFAULT_ELEMENT.removeEventListener(e as any, handleEvent.current, {
          capture: true,
        })
      })
      eventsBound.current = false
    }
  }

  const start = useCallback<(remote?: boolean) => boolean>(
    (remote?: boolean): boolean => {
      destroyTimeout()
      bindEvents()

      idle.current = false
      prompted.current = false
      remaining.current = 0
      promptTime.current = 0

      if (manager.current && !remote) {
        manager.current.start()
      }

      createTimeout()
      return true
    },
    [tId, idle, timeoutRef, manager],
  )

  const reset = useCallback(
    (remote?: boolean) => {
      destroyTimeout()
      bindEvents()

      idleTime.current += Date.now() - (lastIdle.current ?? 0)
      totalIdleTime.current += Date.now() - (lastIdle.current ?? 0)
      idleTime.current = 0
      idle.current = false
      prompted.current = false
      remaining.current = 0
      promptTime.current = 0

      if (manager.current && !remote) {
        manager.current.reset()
      }

      createTimeout()
    },
    [tId, idle, timeoutRef, manager],
  )

  const isIdle = useCallback<() => boolean>((): boolean => {
    return idle.current
  }, [idle])

  const isPrompted = useCallback<() => boolean>((): boolean => {
    return prompted.current
  }, [prompted])

  const isLastActiveTab = useCallback<() => boolean>((): boolean => {
    if (!manager.current) return false
    return manager.current.isLastActive
  }, [manager])

  const getTabId = useCallback<() => string>((): string => {
    if (!manager.current) return ''
    return manager.current.token
  }, [manager])

  const getRemainingTime = useCallback(() => {
    const timeoutTotal = remaining.current ? remaining.current : promptTimeoutRef.current + timeoutRef.current

    const timeSinceLastActive = lastActive.current ? Date.now() - lastActive.current : 0

    const timeLeft = Math.floor(timeoutTotal - timeSinceLastActive)
    return timeLeft < 0 ? 0 : Math.abs(timeLeft)
  }, [timeoutRef, promptTimeoutRef, prompted, remaining, lastActive])

  useEffect(() => {
    if (manager.current) {
      manager.current.close()
    }

    manager.current = new TabManager({
      channelName: 'idle-timer',
      onPrompt: () => {
        togglePrompted()
      },
      onIdle: () => {
        toggleIdle()
      },
      onActive: (remote?: boolean) => {
        toggleActive(remote)
      },
      start,
      reset,
    })
  }, [emitOnIdle, emitOnPrompt, start])

  useEffect(() => {
    if (!firstLoad.current) {
      destroyTimeout()
      unbindEvents(true)
    }
    start()
  }, [firstLoad])

  useEffect(() => {
    if (firstLoad.current) firstLoad.current = false
  }, [firstLoad])

  const idleTimer = {
    isIdle,
    isPrompted,
    getTabId,
    isLastActiveTab,
    getRemainingTime,
    reset,
  }

  return idleTimer
}
